import { Component, OnInit, OnDestroy, inject } from '@angular/core';

import { ToastComponent } from '../shared/toast/toast.component';
import { AuthService } from '../services/auth.service';
import { PoliticianService } from '../services/politician.service';
import { GovernmentService } from '../services/government.service';
import { LegislatureService } from '../services/legislature.service';
import { Politician } from '../shared/models/politician.model';
import {
  ee, roles, rolesMedia, rolesOther, offices, officesNoPortfolio, officesOther,
  collegeRegions, organs, euCountries, clusters, perPage30
} from '../shared/constants';
import { removeTooltipElement, saveFile, scrollTo } from '../shared/functions';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-stakeholders',
  templateUrl: './stakeholders.component.html',
  styleUrls: ['./stakeholders.component.scss'],
  standalone: false
})
export class StakeholdersComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  private politicianService = inject(PoliticianService);
  private legislatureService = inject(LegislatureService);
  private governmentService = inject(GovernmentService);
  toast = inject(ToastComponent);


  isLoading = {
    stakeholders: true,
  };
  isSearching = false;
  canExportCsv = false;
  roles = roles;
  rolesMedia = rolesMedia;
  rolesOther = rolesOther;
  offices = offices;
  officesNoPortfolio = officesNoPortfolio;
  officesOther = officesOther;
  collegeRegions = collegeRegions;
  organs = organs;
  euCountries = euCountries;
  clusters = clusters;
  perPage = perPage30;
  // Filters
  category = ['institutions', 'media', 'corporate'];
  lastName = '';
  job = '';
  jobCommissions = '';
  _governments = '';
  _legislatures = '';
  organ = '';
  _party = '';
  office = '';
  _commissions = '';
  app_stream = '';
  partyLeader = '';
  college = '';
  country = '';
  _media = '';
  notes = '';
  topics = '';
  english = '';
  mediaCompanyFilter = '';
  // Arrays
  politicians: Politician[] = [];
  politicianAdded = false;
  mediaAdded = false;
  count = 0;
  parties = [];
  partiesEU = [];
  commissionsCamera = [];
  commissionsSenato = [];
  commissionsEU = [];
  governments = [];
  legislatures = [];
  medias = [];
  page = 0;
  currentContent = 'stakeholders';
  // If you edit this array, remember to update the next variable
  menuItems = [
    { name: 'All stakeholders', link: 'stakeholders', icon: 'globe' },
    { name: '', link: '', icon: '' }, // Workaround to create a separator
    { name: 'Current government', link: 'current-government', icon: 'globe' },
    { name: 'Current legislature', link: 'current-legislature', icon: 'globe' },
    { name: '', link: '', icon: '' }, // Workaround to create a separator
    { name: 'Deputies', link: 'camera', icon: 'globe' },
    { name: 'Senators', link: 'senato', icon: 'globe' },
    { name: 'MEPs', link: 'eu-parliament', icon: 'globe' },
    { name: '', link: '', icon: '' }, // Workaround to create a separator
    { name: 'Medias', link: 'medias', icon: 'globe' },
    { name: '', link: '', icon: '' }, // Workaround to create a separator
    { name: 'Third parties', link: 'third-parties', icon: 'globe' },
    { name: '', link: '', icon: '' }, // Workaround to create a separator
    { name: 'Parties', link: 'parties', icon: 'flag' },
    { name: 'Ministries / Departments', link: 'ministeri-dipartimenti', icon: 'university' },
    { name: 'Autorities / Agencies', link: 'autorita-agenzie', icon: 'university' },
    { name: '', link: '', icon: '' }, // Workaround to create a separator
    { name: 'Add new Stakeholder', link: 'add-new-stakeholder', icon: 'user-plus' },
    { name: 'Add new Media Company', link: 'add-new-media', icon: 'plus-circle' },
  ];
  // Last 2 items does not show the stakeholders list but it's for adding new stakeholders
  menuItemsStakeholders = this.menuItems.map(item => item.link).slice(0, this.menuItems.length - 2);

  ngOnInit(): void {
    ee.on('changeContent', (data: string) => this.changeContentFn(data));
    ee.on('getStakeholders', (page?: number) => this.search(page));
    ee.on('getMedias', () => this.getMedias());
    ee.on('stakeholderAdded', (politicianAdded: boolean) => this.politicianAdded = politicianAdded);
    ee.on('mediaAdded', (mediaAdded: boolean) => this.mediaAdded = mediaAdded);
    this.canExportCsv = this.auth.currentUser.permissions.includes('exportCsv');
    this.search();
    this.getParties();
    this.getMedias();
    this.getCommissions();
    this.getGovernments();
    this.getLegislatures();
  }

  ngOnDestroy(): void {
    ee.off('changeContent', () => {});
    ee.off('getStakeholders', () => {});
    ee.off('getMedias', () => {});
    ee.off('stakeholderAdded', () => {});
    ee.off('mediaAdded', () => {});
    removeTooltipElement();
  }

  changeContentFn(content: string) {
    this.currentContent = content;
    this.politicianAdded = false;
    this.mediaAdded = false;
    this.resetFilters();
    scrollTo(0);
    switch (content) {
      case 'stakeholders':
        this.resetFilters();
        this.search();
        break;
      case 'current-government':
        this._governments = '63243f821787de666a599b4a';
        this.search();
        break;
      case 'current-legislature':
        this._legislatures = '63243fbc1787de666a59df17';
        this.search();
        break;
      case 'camera':
        this.organ = 'Camera';
        this.search();
        break;
      case 'senato':
        this.organ = 'Senato';
        this.search();
        break;
      case 'eu-parliament':
        this.organ = 'European Parliament';
        this.search();
        break;
      case 'medias':
        this.category = ['media'];
        this.search();
        break;
      case 'third-parties':
        this.category = ['corporate'];
        this.search();
        break;
      case 'parties':
        this.job = 'Partito';
        this.search();
        break;
      case 'ministeri-dipartimenti':
        this.job = 'Ministero / Dipartimento';
        this.search();
        break;
      case 'autorita-agenzie':
        this.job = 'Autorità / Agenzia';
        this.search();
        break;
    }
  }

  resetFilters(): void {
    this.category = ['institutions', 'media', 'corporate'];
    this.job = '';
    this.jobCommissions = '';
    this._governments = '';
    this._legislatures = '';
    this.organ = '';
    this._party = '';
    this.office = '';
    this._commissions = '';
    this.app_stream = '';
    this.partyLeader = '';
    this.college = '';
    this.country = '';
    this._media = '';
    this.notes = '';
    this.topics = '';
    this.english = '';
    this.mediaCompanyFilter = '';
  }

  search(page?: number, csv?: boolean): void {
    this.isLoading.stakeholders = true;
    this.isSearching = true;
    this.page = page || 0;
    const euPartySuffix: 'eu' | '' = this.partiesEU.map(p => p._id).includes(this._party) ? 'eu' : '';
    const params = {
      category: this.category,
      lastName: this.lastName,
      job: this.job,
      jobCommissions: this.jobCommissions,
      _governments: this._governments,
      _legislatures: this._legislatures,
      organ: this.organ,
      _party: this._party,
      euPartySuffix,
      office: this.office,
      _commissions: this._commissions,
      app_stream: this.app_stream,
      partyLeader: this.partyLeader,
      college: this.college,
      country: this.country,
      _media: this._media,
      notes: this.notes,
      topics: this.topics,
      english: this.english,
      csv: csv || false,
      page: this.page,
      perPage: this.perPage,
    };
    this.politicianService.search(params)
      .pipe(finalize(() => this.isLoading.stakeholders = false))
      .subscribe({
        next: res => {
          if (csv) {
            saveFile(res, 'stakeholders.csv');
          } else {
            this.politicians = res.results;
            this.count = res.count;
          }
        },
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  checkCategory(category: string): void {
    if (!this.category.includes(category)) {
      this.category.push(category);
    } else {
      const index = this.category.findIndex(cat => cat === category);
      this.category.splice(index, 1);
    }
    this.search();
  }

  getParties(): void {
    this.politicianService.getParties()
      .subscribe({
        next: res => {
          this.parties = res.filter(party => !party.parlamento_eu);
          this.partiesEU = res.filter(party => party.parlamento_eu);
        },
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getMedias(): void {
    this.politicianService.getMedias()
      .subscribe({
        next: res => this.medias = res,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getCommissions(): void {
    this.politicianService.getCommissions()
      .subscribe({
        next: res => {
          this.commissionsCamera = res.filter(commission => commission.organ === 'camera');
          this.commissionsSenato = res.filter(commission => commission.organ === 'senato');
          this.commissionsEU = res.filter(commission => commission.organ === 'parlamento_eu');
        },
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getGovernments(): void {
    this.governmentService.getAll()
      .subscribe({
        next: res => this.governments = res,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getLegislatures(): void {
    this.legislatureService.getAll()
      .subscribe({
        next: res => this.legislatures = res,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

}
